import { ISOPRO, ISOPROProduct } from './product';
import { WallStructure } from './wallStructure';

const SWEET_SPOT = 0.135;

export enum EnergeticCategory {
  B = 'B',
  A = 'A',
}

type SearchOptions =
  | 'getBiggerLoadBearingCapacityProduct'
  | 'getBiggerShearForceCapacityProduct'
  | 'getSmallerHeightProduct'
  | 'getOneWithoutFireProtection';

type CalculationStrategy =
  | 'By load bearing capacity'
  | 'By shear force capacity'
  | 'By smaller height'
  | 'By lambda value'
  | 'By fire protection'

type CategoryResolver = ({ lambdaValue }: ISOPROProduct) => boolean;

export interface InstallationSetupData {
  product: ISOPRO;
  wallStructure: WallStructure;
  catAThresh: number;
  uThresh: number[];
}

export class InstallationSetup implements InstallationSetupData {
  readonly product: InstallationSetupData['product'];
  readonly wallStructure: InstallationSetupData['wallStructure'];
  readonly catAThresh: InstallationSetupData['catAThresh'];
  readonly uThresh: InstallationSetupData['uThresh'];

  private readonly isCatA: boolean = false;
  private readonly isCatB: boolean;

  calculationType: 'direct' | 'indirect' | 'none' = 'direct';
  calculationStrategy: CalculationStrategy | '' = '';

  constructor (data: InstallationSetupData) {
    this.product = data.product;
    this.wallStructure = data.wallStructure;
    this.catAThresh = data.catAThresh;
    this.uThresh = data.uThresh;

    this.isCatB = this.calculateCategory((prod) => this.checkBCatValue(prod), EnergeticCategory.B);
    if (this.isCatB) return;

    this.isCatA = this.calculateCategory((prod) => this.checkACatValue(prod, this.catAThresh), EnergeticCategory.A);
  }

  get isPrediction (): boolean {
    return !this.product.lambdaValue;
  }

  get category (): EnergeticCategory | null {
    if (this.isCatB) return EnergeticCategory.B;
    return this.isCatA ? EnergeticCategory.A : null;
  }

  private calculateCategory (categoryResolver: CategoryResolver, searchCategory: EnergeticCategory): boolean {
    if (categoryResolver(this.product)) {
      this.calculationStrategy = 'By lambda value'
      this.calculationType = 'direct';
      return true;
    }

    this.calculationType = 'indirect';
    if (!!this.product.capacity) {
      if (this.recursiveSearchOfBCat(this.product, 'getBiggerLoadBearingCapacityProduct', categoryResolver)) {
        this.calculationStrategy = 'By load bearing capacity'
        return true;
      }
    }
    if (this.recursiveSearchOfBCat(this.product, 'getBiggerShearForceCapacityProduct', categoryResolver)) {
      this.calculationStrategy = 'By shear force capacity'
      return true;
    }
    if (this.recursiveSearchOfBCat(this.product, 'getSmallerHeightProduct', categoryResolver)) {
      this.calculationStrategy = 'By smaller height'
      return true;
    }

    const prod = this.product.getOneWithoutFireProtection();

    if (prod) {
      const { category } = new InstallationSetup({
        catAThresh: this.catAThresh,
        product: prod as ISOPRO,
        uThresh: this.uThresh,
        wallStructure: this.wallStructure,
      })
      if (category === searchCategory) {
        this.calculationStrategy = 'By fire protection'
        return true
      }
    }

    this.calculationType = 'none';
    return false;
  }

  private checkACatValue ({ lambdaValue }: ISOPROProduct, catAThresh: number): boolean {
    return !!lambdaValue &&
      lambdaValue > SWEET_SPOT &&
      lambdaValue <= catAThresh;
  }

  private checkBCatValue ({ lambdaValue }: ISOPROProduct): boolean {
    return !!lambdaValue && lambdaValue < SWEET_SPOT;
  }

  private recursiveSearchOfBCat<T extends ISOPROProduct> (product: T, search: SearchOptions, categoryResolver: CategoryResolver): boolean {
    if (search in product) {
      const result = product[search]?.();
      // console.log(search, result);
      if (!result) return false;
      if (result.lambdaValue) return categoryResolver(result);
      return this.recursiveSearchOfBCat(result, search, categoryResolver);
    }
    return false;
  }
}
